import { Button, Input, message as antdMessage, Popover, Switch } from "antd";
import { SendOutlined, SettingOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SystemDescriptions } from "src/common/descriptions";
import { ChatAction, ChatMessage } from "src/common/models/chat";
import { serviceLoading } from "src/common/apiLoader/state/selection";
import { RootState } from "src/state/reducer";
import { sendChatMessageApi } from "./state/action";
import { SEND_CHAT_MESSAGE_API } from "./state/actionTypes";
import * as Styles from "./styles";
import { NetworkingProvider } from "../Security/networking/networkingProvider";
import { Urls } from "src/common/urls";

const SEND_WITH_ENTER_STORAGE_KEY = "prochat.sendWithEnter"

interface ReduxProps {
    messages: ChatMessage[]
    isSending: boolean
    activeQuotation?: {
        quotationId: number
        quotationCode?: string
        documentType?: string
        name?: string
        customerName?: string
    }
}

export const Chat = () => {
    const dispatch = useDispatch()
    const descriptions = SystemDescriptions.PAGES.CHAT
    const [message, setMessage] = useState<string>("")
    const [sendWithEnter, setSendWithEnter] = useState<boolean>(() => (
        localStorage.getItem(SEND_WITH_ENTER_STORAGE_KEY) !== "false"
    ))
    const [downloadingAction, setDownloadingAction] = useState<string | undefined>(undefined)
    const messagesEndRef = useRef<HTMLDivElement | null>(null)

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        messages: state.chat.messages,
        isSending: serviceLoading(state, [SEND_CHAT_MESSAGE_API]),
        activeQuotation: state.chat.activeQuotation,
    }))

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
    }, [reduxProps.messages.length, reduxProps.isSending])

    const sendMessage = (messageToSend?: string) => {
        const finalMessage = (messageToSend ?? message).trim()
        if (!finalMessage) {
            antdMessage.warning(descriptions.EMPTY_ERROR)
            return
        }
        const lowerMessage = finalMessage.toLowerCase()
        const moduleContext = reduxProps.activeQuotation || lowerMessage.includes("cotiz") || lowerMessage.includes("quotation") || lowerMessage.includes("propuesta")
            ? "quotation"
            : "general"

        dispatch(sendChatMessageApi({
            message: finalMessage,
            conversationHistory: reduxProps.messages.filter(chatMessage => chatMessage.role !== "error"),
            provider: "openai",
            context: {
                module: moduleContext,
                activeQuotationId: reduxProps.activeQuotation?.quotationId,
                activeQuotationCode: reduxProps.activeQuotation?.quotationCode,
                activeQuotationDocumentType: reduxProps.activeQuotation?.documentType,
                activeQuotationName: reduxProps.activeQuotation?.name,
                activeQuotationCustomerName: reduxProps.activeQuotation?.customerName,
            },
        }))
        setMessage("")
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key !== "Enter") {
            return
        }

        const shouldSend = sendWithEnter
            ? !event.shiftKey
            : event.shiftKey

        if (shouldSend) {
            event.preventDefault()
            sendMessage()
        }
    }

    const updateSendWithEnter = (enabled: boolean) => {
        setSendWithEnter(enabled)
        localStorage.setItem(SEND_WITH_ENTER_STORAGE_KEY, String(enabled))
    }

    const renderEmptyState = () => (
        <Styles.EmptyState>
            <Styles.ProChatMark aria-hidden="true">
                <Styles.ProChatCore />
            </Styles.ProChatMark>
            <Styles.WelcomeTitle>{descriptions.WELCOME_TITLE}</Styles.WelcomeTitle>
            <Styles.WelcomeSubtitle>{descriptions.WELCOME_SUBTITLE}</Styles.WelcomeSubtitle>
        </Styles.EmptyState>
    )

    const sanitizeAssistantText = (content: string): string => (
        content
            .replace(/!\[[^\]]*]\([^)]*\)/g, "")
            .replace(/\[[^\]]*]\((https?:\/\/[^)]*)\)/g, "")
            .replace(/https?:\/\/\S+/g, "")
            .replace(/\$(\s?\d)/g, "Q$1")
            .split("\n")
            .map(line => line.trimEnd())
            .filter(line => line.trim() !== "-")
            .join("\n")
            .trim()
    )

    const renderInlineText = (text: string) => {
        const parts = text.split(/(\*\*[^*]+\*\*)/g)
        return parts.map((part, index) => {
            if (part.startsWith("**") && part.endsWith("**")) {
                return <strong key={index}>{part.slice(2, -2)}</strong>
            }
            return <span key={index}>{part}</span>
        })
    }

    const renderContentLine = (line: string, index: number) => {
        const listMatch = line.match(/^(\d+\.|-)\s+(.*)$/)
        if (listMatch) {
            return (
                <Styles.ListLine key={index}>
                    <span>{listMatch[1]}</span>
                    <span>{renderInlineText(listMatch[2])}</span>
                </Styles.ListLine>
            )
        }

        return <p key={index}>{renderInlineText(line)}</p>
    }

    const renderMessageContent = (chatMessage: ChatMessage) => {
        const content = chatMessage.role === "assistant"
            ? sanitizeAssistantText(chatMessage.content)
            : chatMessage.content
        const lines = content.split("\n").filter(line => line.trim().length > 0)

        return (
            <Styles.MessageContent>
                {lines.map(renderContentLine)}
            </Styles.MessageContent>
        )
    }

    const downloadQuotationPdf = async (action: ChatAction) => {
        if (!action.payload.templateId) {
            return
        }

        const actionKey = `${action.payload.quotationId}-${action.payload.templateId}`
        setDownloadingAction(actionKey)
        try {
            const response = await NetworkingProvider.getInstance().getControlInterface().post(
                Urls.BACKEND.QUOTATION.PDF(action.payload.quotationId),
                {
                    templateId: action.payload.templateId,
                    showPrices: action.payload.showPrices,
                },
                { responseType: "blob" },
            )

            const url = window.URL.createObjectURL(response.data as any)
            const a = document.createElement("a")
            a.href = url
            a.download = action.payload.fileName || `quotation-${action.payload.quotationId}.pdf`
            document.body.appendChild(a)
            a.click()
            a.remove()
            window.URL.revokeObjectURL(url)
        } catch {
            antdMessage.error(descriptions.DOWNLOAD_ERROR)
        } finally {
            setDownloadingAction(undefined)
        }
    }

    const renderActions = (chatMessage: ChatMessage) => {
        if (!chatMessage.actions || chatMessage.actions.length === 0) {
            return null
        }

        return (
            <Styles.Actions>
                {
                    chatMessage.actions.map((action, index) => {
                        if (action.type !== "DOWNLOAD_QUOTATION_PDF") {
                            return null
                        }

                        const actionKey = `${action.payload.quotationId}-${action.payload.templateId}`
                        return (
                            <Button
                                key={`${action.type}-${index}`}
                                size="small"
                                type="primary"
                                ghost
                                loading={downloadingAction === actionKey}
                                onClick={() => downloadQuotationPdf(action)}
                            >
                                {action.label || descriptions.DOWNLOAD_PDF}
                            </Button>
                        )
                    })
                }
            </Styles.Actions>
        )
    }

    const renderMessage = (chatMessage: ChatMessage, index: number) => (
        <Styles.MessageRow key={`${chatMessage.role}-${index}`} role={chatMessage.role}>
            <Styles.MessageBubble role={chatMessage.role}>
                {renderMessageContent(chatMessage)}
                {renderActions(chatMessage)}
            </Styles.MessageBubble>
        </Styles.MessageRow>
    )

    const renderThinking = () => (
        <Styles.MessageRow role="assistant">
            <Styles.MessageBubble role="assistant">
                <Styles.ThinkingDots>{descriptions.THINKING}</Styles.ThinkingDots>
            </Styles.MessageBubble>
        </Styles.MessageRow>
    )

    const renderMessages = () => (
        <Styles.Messages>
            {reduxProps.messages.map(renderMessage)}
            {reduxProps.isSending && renderThinking()}
            <Styles.MessagesEnd ref={messagesEndRef} />
        </Styles.Messages>
    )

    const renderComposer = () => (
        <Styles.Composer>
            <Styles.ComposerInner>
                <Input.TextArea
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={descriptions.INPUT_PLACEHOLDER}
                    autoSize={{ minRows: 1, maxRows: 5 }}
                    disabled={reduxProps.isSending}
                    bordered={false}
                />
                <Popover
                    trigger="click"
                    placement="topRight"
                    content={
                        <Styles.PreferencePanel>
                            <Styles.PreferenceTitle>{descriptions.PREFERENCES_TITLE}</Styles.PreferenceTitle>
                            <Styles.PreferenceRow>
                                <span>{descriptions.SEND_WITH_ENTER}</span>
                                <Switch
                                    size="small"
                                    checked={sendWithEnter}
                                    onChange={updateSendWithEnter}
                                />
                            </Styles.PreferenceRow>
                            <Styles.PreferenceHelp>{descriptions.SEND_WITH_ENTER_HELP}</Styles.PreferenceHelp>
                        </Styles.PreferencePanel>
                    }
                >
                    <Button
                        type="text"
                        shape="circle"
                        icon={<SettingOutlined />}
                        className="composer-settings-button"
                        aria-label={descriptions.PREFERENCES_TITLE}
                    />
                </Popover>
                <Button
                    type="primary"
                    shape="circle"
                    icon={<SendOutlined />}
                    className="composer-send-button"
                    loading={reduxProps.isSending}
                    onClick={() => sendMessage()}
                    aria-label={descriptions.SEND_BUTTON}
                />
            </Styles.ComposerInner>
            <Styles.ComposerMeta>
                <span>
                    {sendWithEnter ? descriptions.SEND_HINT_ENTER : descriptions.SEND_HINT_SHIFT_ENTER}
                </span>
                <span>{descriptions.WARNING}</span>
            </Styles.ComposerMeta>
        </Styles.Composer>
    )

    return (
        <Styles.ChatPage>
            <Styles.ChatShell>
                {reduxProps.messages.length === 0 ? renderEmptyState() : renderMessages()}
                {renderComposer()}
            </Styles.ChatShell>
        </Styles.ChatPage>
    )
}
